import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  DropdownComponent,
  DropdownItem,
  ResponsiveService,
  SearchComponent,
} from '@frk/eds-components';
import {
  CapGainsEstimatesFilterType,
  CapGainsEstimatesRecordDetails,
  CapGainsEstimatesSelectedFilters,
} from '@products/types/tax.type';
import { Observable, Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'ft-us-capital-gains-estimates-filters',
  templateUrl: './us-capital-gains-estimates-filters.component.html',
  styleUrls: ['./us-capital-gains-estimates-filters.component.scss'],
})
export class UsCapitalGainsEstimatesFiltersComponent
  implements OnInit, OnChanges, OnDestroy {
  @Input() filters: CapGainsEstimatesFilterType[];
  @Input() recordDetails: CapGainsEstimatesRecordDetails;
  @Output()
  filterListing = new EventEmitter<CapGainsEstimatesSelectedFilters>();
  isHandheld$: Observable<boolean>;
  debouncer$: Subject<string> = new Subject<string>();
  componentDestroyed$: Subject<boolean> = new Subject<boolean>();
  productTypeFilter: CapGainsEstimatesFilterType;
  otherFilterTypes: CapGainsEstimatesFilterType[];
  selectedFilters: CapGainsEstimatesSelectedFilters = {
    selectedDropdowns: [],
    searchedText: '',
  };
  totalRecords: number;
  filteredRecords: number;
  isAnyFilterPresent = false;

  @ViewChildren('filterDropdowns')
  filterDropdowns: QueryList<DropdownComponent>;
  @ViewChild('filterSearch') filterSearch: SearchComponent;

  constructor(private responsiveService: ResponsiveService) {}

  ngOnInit(): void {
    // Used 'debounce' to ignore search input within 500 ms of key down.
    // Further, used 'distinctUntilChanged' to avoid subscription, if final search term is equal to the original one.
    // This will avoid unnecessary service call.
    this.debouncer$
      .pipe(
        takeUntil(this.componentDestroyed$),
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe((searchedText: string) => {
        this.selectedFilters.searchedText = searchedText;

        // WDE-2971: Make search box to filter result either with zero or greater than two characters input.
        if (![1, 2].includes(searchedText.length)) {
          this.filterListing.next(this.selectedFilters);
        }
      });

    this.isHandheld$ = this.responsiveService.isHandheld$();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filters?.currentValue) {
      const updatedFilters: CapGainsEstimatesFilterType[] =
        changes.filters.currentValue;

      this.isAnyFilterPresent = updatedFilters.length > 0;

      this.productTypeFilter = updatedFilters?.find(
        (filterType) => filterType.filterName === 'productType'
      );
      this.otherFilterTypes = updatedFilters?.filter(
        (filterType) => filterType.filterName !== 'productType'
      );
    }

    if (changes.recordDetails?.currentValue) {
      const updatedRecordDetails: CapGainsEstimatesRecordDetails =
        changes.recordDetails.currentValue;

      this.totalRecords = updatedRecordDetails?.total;
      this.filteredRecords = updatedRecordDetails?.filtered;
    }
  }

  /**
   * Filter the cap gain listing based upon filter type & item(s) selected.
   * @param selectedFilterName name of the filter type selected
   * @param selectedDropdownItems item(s) of the filter type selected
   */
  onDropdownChange(
    selectedFilterName: string,
    selectedDropdownItems: DropdownItem[]
  ): void {
    // Extract the filter type, if already present in the selected list.
    let selectedFilterType: CapGainsEstimatesFilterType;
    let selectedFilterTypeIndex: number;

    this.selectedFilters.selectedDropdowns.find((selectedDropdown, index) => {
      if (selectedDropdown.filterName === selectedFilterName) {
        selectedFilterType = selectedDropdown;
        selectedFilterTypeIndex = index;
      }
    });

    // If current filter dropdown found in selection list, save the updated selection(s) accordingly.
    if (selectedFilterType) {
      // But, also check if 'selectedDropdownItems' isn't empty.
      if (selectedDropdownItems.length) {
        selectedFilterType.filterItems = selectedDropdownItems;
      }
      // If Empty, it means, there has been uncheck event with zero item selected.
      // Hence, remove this filter type dropdown from selected filters list.
      else {
        this.selectedFilters.selectedDropdowns.splice(
          selectedFilterTypeIndex,
          1
        );
      }
    }
    // If not found, it means, new filter type has been selected.
    // Hence, save it into selected filters list.
    else {
      this.selectedFilters.selectedDropdowns.push({
        filterName: selectedFilterName,
        filterItems: selectedDropdownItems,
      });
    }

    // Publish the filter selection(s) to service.
    this.filterListing.next(this.selectedFilters);
  }

  /**
   * Filter the cap gain listing based upon search text entered.
   * @param searchedText string entered in search box
   */
  onSearchInput(searchedText: string): void {
    this.debouncer$.next(searchedText.trim());
  }

  /**
   * Reset the filters & search and hence reset the grid listing.
   * @param event event generated by link click.
   */
  onFilterReset(event: PointerEvent): void {
    // Avoid reload of page as the default behaviour.
    event.preventDefault();

    // Reset the 'dropdown' and 'search' components.
    this.filterDropdowns.forEach((dropdown) => dropdown.reset());
    this.filterSearch.value = '';
    this.filterSearch.hasTyped = false;

    // Default the filter dropdown selection(s)/search to empty.
    this.selectedFilters = {
      selectedDropdowns: [],
      searchedText: '',
    };

    // Finally, publish the reset event with default filter selections to service.
    this.filterListing.next(this.selectedFilters);
  }

  ngOnDestroy(): void {
    // Unsubscribe from Observables
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
